import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, GlobeAltIcon, CalendarIcon, ExclamationIcon } from '@heroicons/react/solid';
import WideBlankModal from './WideBlankModal';
import ApiRequest from '../../api/ApiRequest';
import { API_AUTH_TYPES, API_ROUTES } from '../../api/apiConstants';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, startOfMonth, startOfToday, isBefore, isToday, isSameMonth, isSameDay, endOfMonth, eachDayOfInterval } from 'date-fns'
import { DateTime } from 'luxon';

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const SchedulingModal = ({ open, setOpen }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [allAvailableTimes, setAllAvailableTimes] = useState([]);
    const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [currentTime, setCurrentTime] = useState(DateTime.now());
    const [showTimeSlots, setShowTimeSlots] = useState(false); // For mobile navigation
    const queryClient = useQueryClient();

    // Create API instance outside of render to avoid recreation
    const prospectAssignmentApi = new ApiRequest(API_ROUTES.PROSPECT_ASSIGNMENT, API_AUTH_TYPES.NONE)

    // Update current time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(DateTime.now());
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, []);

    console.log('format', format, startOfMonth, startOfToday, isBefore, isToday, isSameMonth, isSameDay)

    // Load all 6 months of cached data
    const availableTimes = useQuery({
        queryKey: ['availableTimes'],
        queryFn: prospectAssignmentApi.getFn({
            endpoint: 'available_times'
        }),
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        refetchOnWindowFocus: true,
        onSuccess: (data) => {
            setAllAvailableTimes(data.availableTimes || []);
        },
        onError: (error) => {
            console.error('Query error:', error);
        }
    });

    // Check if current month has any available times
    const hasAvailableTimesInMonth = useCallback(() => {
        if (!allAvailableTimes?.length) return false;

        return allAvailableTimes.some(slot => {
            const slotDate = new Date(slot.start_time);
            return isSameMonth(slotDate, currentMonth) && !isBefore(slotDate, startOfToday());
        });
    }, [allAvailableTimes, currentMonth]);

    // Auto-advance to next month with available times when modal opens
    useEffect(() => {
        if (open && !availableTimes.isLoading && allAvailableTimes?.length > 0) {
            const findNextMonthWithTimes = () => {
                let checkMonth = new Date(currentMonth);
                const maxMonthsToCheck = 6; // Limit to prevent infinite loop

                for (let i = 0; i < maxMonthsToCheck; i++) {
                    const currentCheckMonth = new Date(checkMonth); // Create a new date for each iteration
                    const hasTimesInMonth = allAvailableTimes.some(slot => {
                        const slotDate = new Date(slot.start_time);
                        return isSameMonth(slotDate, currentCheckMonth) && !isBefore(slotDate, startOfToday());
                    });

                    if (hasTimesInMonth) {
                        return currentCheckMonth;
                    }

                    // Move to next month
                    checkMonth.setMonth(checkMonth.getMonth() + 1);
                }

                return null; // No available times found in next 6 months
            };

            // Only auto-advance if current month has no available times
            if (!hasAvailableTimesInMonth()) {
                const nextAvailableMonth = findNextMonthWithTimes();
                if (nextAvailableMonth && !isSameMonth(nextAvailableMonth, currentMonth)) {
                    setCurrentMonth(nextAvailableMonth);
                    setSelectedDate(null);
                    setTimeSlots([]);
                    setSelectedTime(null);
                    setShowTimeSlots(false);
                }
            }
        }
    }, [open, availableTimes.isLoading, allAvailableTimes, currentMonth, hasAvailableTimesInMonth]);

    // Get days in current month
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Get days from previous month to fill the first week
    const firstDayOfWeek = monthStart.getDay();
    const previousMonthDays = [];
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = new Date(monthStart);
        day.setDate(day.getDate() - (i + 1));
        previousMonthDays.push(day);
    }

    // Get days from next month to fill the last week
    const lastDayOfWeek = monthEnd.getDay();
    const nextMonthDays = [];
    for (let i = 1; i <= (6 - lastDayOfWeek); i++) {
        const day = new Date(monthEnd);
        day.setDate(day.getDate() + i);
        nextMonthDays.push(day);
    }

    const allDays = [...previousMonthDays, ...monthDays, ...nextMonthDays];

    // Check if a date has available times
    const hasAvailableTimes = (date) => {
        if (!allAvailableTimes?.length) return false;

        return allAvailableTimes.some(slot => {
            const slotDate = new Date(slot.start_time);
            return isSameDay(slotDate, date);
        });
    };

    // Get time slots for selected date
    const getTimeSlotsForDate = (date) => {
        if (!allAvailableTimes?.length) return [];

        return allAvailableTimes
            .filter(slot => {
                const slotDate = new Date(slot.start_time);
                return isSameDay(slotDate, date);
            })
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    };

    // Format time in selected timezone
    const formatTimeInTimezone = (datetime, timezone) => {
        return DateTime.fromJSDate(new Date(datetime))
            .setZone(timezone)
            .toFormat('h:mm a');
    };

    // Get current time in selected timezone
    const getCurrentTimeInTimezone = (timezone) => {
        return currentTime.setZone(timezone).toFormat('h:mm a');
    };

    // Handle date selection
    const handleDateClick = (date) => {
        if (isBefore(date, startOfToday())) return;

        setSelectedDate(date);
        const slots = getTimeSlotsForDate(date);
        setTimeSlots(slots);
        setSelectedTime(null);
        setShowTimeSlots(true); // Show time slots on mobile
    };

    // Handle back to calendar on mobile
    const handleBackToCalendar = () => {
        setShowTimeSlots(false);
        setSelectedDate(null);
        setTimeSlots([]);
        setSelectedTime(null);
    };

    // Handle time selection
    const handleTimeSelect = (slot) => {
        setSelectedTime(slot);
    };

    // Handle timezone change
    const handleTimezoneChange = (e) => {
        setSelectedTimezone(e.target.value);
    };

    // Mutation to remove booked time slot
    const removeBookedSlotMutation = useMutation({
        mutationFn: async (slotData) => {
            return prospectAssignmentApi.post({
                endpoint: 'remove_booked_slot',
                body: slotData
            });
        },
        onSuccess: () => {
            // Invalidate the available times query to refresh the data
            queryClient.invalidateQueries(['availableTimes']);
            console.log('Successfully removed booked time slot');
        },
        onError: (error) => {
            console.error('Error removing booked time slot:', error);
        }
    });

    // Handle booking
    const handleBooking = async () => {
        if (selectedTime && selectedTime.scheduling_url) {
            // Remove the time slot from Redis
            try {
                await removeBookedSlotMutation.mutateAsync({
                    start_time: selectedTime.start_time,
                    advisor_id: selectedTime.advisor_id
                });

                // Open the Calendly booking URL
                window.open(selectedTime.scheduling_url, '_blank');
                setOpen(false);
            } catch (error) {
                // Even if removal fails, still open the booking URL
                console.error('Failed to remove time slot, but proceeding with booking:', error);
                window.open(selectedTime.scheduling_url, '_blank');
                setOpen(false);
            }
        }
    };

    // Check if we can go to previous month
    const canGoToPreviousMonth = () => {
        const today = new Date();
        const currentMonthStart = startOfMonth(currentMonth);
        const thisMonthStart = startOfMonth(today);
        return currentMonthStart > thisMonthStart;
    };

    // Navigate months
    const previousMonth = () => {
        if (!canGoToPreviousMonth()) return;

        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            newMonth.setMonth(prev.getMonth() - 1);
            return newMonth;
        });
        setSelectedDate(null);
        setTimeSlots([]);
        setSelectedTime(null);
        setShowTimeSlots(false);
    };

    const nextMonth = () => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            newMonth.setMonth(prev.getMonth() + 1);
            return newMonth;
        });
        setSelectedDate(null);
        setTimeSlots([]);
        setSelectedTime(null);
        setShowTimeSlots(false);
    };

    return (
        <WideBlankModal open={open} setOpen={setOpen}>
            <div className="max-w-5xl mx-auto">
                {/* Custom Title */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 rounded-full shadow-lg">
                            <CalendarIcon className="h-8 w-8 text-white" />
                        </div>
                    </div>
                    <h2 className="text-lg sm:text-base xl:text-2xl font-bold text-gray-900 mb-2">
                        Schedule Your Discovery Meeting
                    </h2>
                    <p className="text-xs sm:text-sm xl:text-lg text-gray-600 max-w-full sm:max-w-2xl mx-auto px-2 sm:px-0">
                        Select a date and time for your free, no-pressure consultation.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Calendar - Hidden on mobile when showing time slots */}
                    <div className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm ${showTimeSlots ? 'hidden lg:block' : 'block'}`}>
                        {/* Month navigation */}
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={previousMonth}
                                disabled={!canGoToPreviousMonth()}
                                className={`p-2 rounded-full transition-colors duration-200 ${canGoToPreviousMonth()
                                    ? 'hover:bg-gray-100 cursor-pointer'
                                    : 'cursor-not-allowed opacity-40'
                                    }`}
                            >
                                <ChevronLeftIcon className={`h-5 w-5 ${canGoToPreviousMonth() ? 'text-gray-600' : 'text-gray-400'
                                    }`} />
                            </button>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {format(currentMonth, 'MMMM yyyy')}
                            </h2>
                            <button
                                onClick={nextMonth}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                            >
                                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                            </button>
                        </div>

                        {/* Days of week */}
                        <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-3">
                            <div className="py-2">SUN</div>
                            <div className="py-2">MON</div>
                            <div className="py-2">TUE</div>
                            <div className="py-2">WED</div>
                            <div className="py-2">THU</div>
                            <div className="py-2">FRI</div>
                            <div className="py-2">SAT</div>
                        </div>

                        {/* Calendar days */}
                        <div className="grid grid-cols-7 gap-1">
                            {allDays.map((day, idx) => {
                                const isCurrentMonth = isSameMonth(day, currentMonth);
                                const isPast = isBefore(day, startOfToday());
                                const hasSlots = hasAvailableTimes(day);
                                const isSelected = selectedDate && isSameDay(day, selectedDate);
                                const isCurrentDay = isToday(day);

                                return (
                                    <button
                                        key={idx}
                                        onClick={() => handleDateClick(day)}
                                        disabled={isPast || !hasSlots || !isCurrentMonth}
                                        className={`
                      relative h-11 w-full flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200
                      ${!isCurrentMonth ? 'text-gray-300' : ''}
                      ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                      ${hasSlots && isCurrentMonth && !isPast ? 'hover:bg-blue-50 hover:text-blue-700 cursor-pointer' : ''}
                      ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg scale-105' : ''}
                      ${isCurrentDay && !isSelected ? 'bg-gray-100 text-gray-900' : ''}
                      ${hasSlots && isCurrentMonth && !isPast && !isSelected ? 'text-blue-600 hover:shadow-md' : ''}
                    `}
                                    >
                                        {format(day, 'd')}
                                        {hasSlots && isCurrentMonth && !isPast && (
                                            <div className={`absolute bottom-1.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-blue-500'}`}></div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Loading indicator */}
                        {availableTimes.isLoading && (
                            <div className="mt-6 text-center text-gray-500 flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span>Loading available times...</span>
                            </div>
                        )}
                    </div>

                    {/* Time slots - Hidden on mobile when showing calendar */}
                    <div className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm ${!showTimeSlots ? 'hidden lg:block' : 'block'}`}>
                        {/* Mobile Back Button */}
                        {showTimeSlots && (
                            <div className="lg:hidden mb-6">
                                <button
                                    onClick={handleBackToCalendar}
                                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                                >
                                    <ChevronLeftIcon className="h-5 w-5" />
                                    <span className="text-sm font-medium">Back to Calendar</span>
                                </button>
                            </div>
                        )}

                        {!availableTimes.isLoading && !hasAvailableTimesInMonth() ? (
                            /* No slots available in current month */
                            <div className="text-center py-12">
                                <div className="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                                    <ExclamationIcon className="h-10 w-10 text-gray-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No appointments available in {format(currentMonth, 'MMMM yyyy')}
                                </h3>

                                <div className="flex items-center justify-center space-x-4">
                                    <button
                                        onClick={previousMonth}
                                        disabled={!canGoToPreviousMonth()}
                                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${canGoToPreviousMonth()
                                            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 cursor-pointer'
                                            : 'text-gray-400 cursor-not-allowed opacity-50'
                                            }`}
                                    >
                                        <ChevronLeftIcon className="h-4 w-4" />
                                        <span>Previous Month</span>
                                    </button>
                                    <button
                                        onClick={nextMonth}
                                        className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                    >
                                        <span>Next Month</span>
                                        <ChevronRightIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ) : selectedDate ? (
                            <>
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                        {format(selectedDate, 'EEEE, MMMM d')}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Select your preferred time
                                    </p>
                                </div>

                                {timeSlots.length > 0 ? (
                                    <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar px-1 py-1">
                                        {timeSlots.map((slot, idx) => {
                                            const isSelected = selectedTime === slot;
                                            const isProcessing = removeBookedSlotMutation.isLoading && isSelected;

                                            return (
                                                <div key={idx} className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                                                    <button
                                                        onClick={() => handleTimeSelect(slot)}
                                                        className={`
                                            transition-all duration-300 ease-in-out py-4 px-4 rounded-lg border text-center flex items-center justify-center space-x-2 font-medium
                                            ${isSelected ? 'sm:w-32 sm:flex-shrink-0' : 'flex-1'}
                                            ${isSelected
                                                                ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-200'
                                                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 hover:shadow-sm text-gray-900'
                                                            }
                                        `}
                                                    >
                                                        <ClockIcon className="h-4 w-4 flex-shrink-0" />
                                                        <span className="truncate whitespace-nowrap">{formatTimeInTimezone(slot.start_time, selectedTimezone)}</span>
                                                    </button>

                                                    {/* Continue Button - Mobile: below, Desktop: slide-in */}
                                                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isSelected
                                                        ? 'w-full sm:w-48 opacity-100'
                                                        : 'w-0 opacity-0 sm:block hidden'
                                                        }`}>
                                                        <button
                                                            onClick={handleBooking}
                                                            disabled={isProcessing}
                                                            className="w-full sm:w-48 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors duration-200 font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm whitespace-nowrap"
                                                        >
                                                            {isProcessing ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                    <span>Processing...</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span>Continue to Booking</span>
                                                                    <ChevronRightIcon className="h-4 w-4" />
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500">No available times for this date.</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                    <ClockIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">Select a date to see available times</p>
                                <p className="text-sm text-gray-400 mt-1">Choose from the calendar on the left</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Timezone Selector - Mobile Responsive */}
                <div className="mt-8 flex items-center justify-center  px-4">
                    <div className="w-full  bg-gray-50  max-w-xl px-4 sm:px-6 py-4 sm:py-4 rounded-xl border shadow-sm">
                        {/* Mobile: Stacked Layout, Desktop: Horizontal Layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-6">
                            {/* Timezone Selection */}
                            <div className="flex items-center justify-center sm:justify-start space-x-3">
                                <GlobeAltIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Timezone:</label>
                                <select
                                    value={selectedTimezone}
                                    onChange={handleTimezoneChange}
                                    className="border-0 bg-transparent text-sm font-semibold text-gray-900 focus:ring-0 focus:outline-none cursor-pointer min-w-0 flex-1 sm:flex-initial"
                                >
                                    {COMMON_TIMEZONES.map((tz) => (
                                        <option key={tz.value} value={tz.value}>
                                            {tz.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Current Time Display */}
                            <div className="flex items-center justify-start sm:justify-end space-x-2 pt-4 sm:pt-0 border-t sm:border-t-0 sm:border-l border-gray-300 sm:pl-6">
                                <ClockIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <span className="text-sm text-gray-600 whitespace-nowrap">Current time:</span>
                                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                    {getCurrentTimeInTimezone(selectedTimezone)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom scrollbar styles */}
            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            `}</style>
        </WideBlankModal>
    );
};

export default SchedulingModal; 